import { BarChartUrl } from '@icons';
import { Carousel } from '@ui';
import { useStateList } from 'react-use';
import { useMemo } from 'react';
import { ClinicalGalleryItem, ClinicalSection, TimeStepper } from '@features/components/ClinicalData/components';
import { convertMediaFiles } from '@features/components/ClinicalData/utils';
import { VitalsGrid } from '@features/components/ClinicalData/components/VitalsGrid';
import { usePreviewFile } from '@features/components/ClinicalData/hooks';
import { MediaPreview } from '@features/components/ClinicalData/components/MediaPreview/MediaPreview';
import { formatVitals } from './utils';
import { ClinicalData, OrganType } from '@/api/apis/case';
import { getColorsByOrganType } from '@/utils/helpers';

const label = 'Vitals';

export const Vitals = ({ organType, clinicalData }: { organType: OrganType; clinicalData: ClinicalData }) => {
  const colors = getColorsByOrganType(organType);
  const vitals = formatVitals(clinicalData);
  const { state, prev, next, currentIndex, isFirst, isLast } = useStateList(vitals);
  const mediaList = useMemo(
    () => vitals.flatMap((item) => (item.file ? convertMediaFiles([item.file], item.timestamp, item.timeZone) : [])),
    [vitals],
  );
  const { isPreviewOpen, previewFileId, handlePreviewOpen, handlePreviewClose } = usePreviewFile();

  return (
    <>
      <ClinicalSection
        className="row-span-1"
        color={colors.bg40}
        header={label}
        icon={BarChartUrl}
        endContent={
          !!vitals.length && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs-r text-base-20">
                {vitals.length} Reading{vitals.length === 1 ? '' : 's'}
              </span>
              <TimeStepper
                timezone={state?.timeZone}
                timestamp={state?.timestamp}
                isFirst={isFirst}
                isLast={isLast}
                onNext={next}
                onPrev={prev}
              />
            </div>
          )
        }
      >
        {vitals.length > 0 && (
          <div className={`scroll w-full ${organType}`}>
            <div className="flex justify-start gap-2 pb-1 relative w-full overflow-hidden">
              <Carousel currentIndex={currentIndex} itemClassName="w-full">
                {vitals.map((v) => (
                  <div key={v.timestamp} className="flex items-center gap-4 w-full">
                    <VitalsGrid vitals={v.vitalParameters} />
                    <ClinicalGalleryItem
                      file={v.file}
                      topLabel={`${v.measurementMinute ?? 0} min`}
                      topLabelClassName="bg-white text-base-5!"
                      onClick={() => handlePreviewOpen(v.file?.fileId || '')}
                    />
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
        )}
      </ClinicalSection>
      {isPreviewOpen && (
        <MediaPreview
          heading={label}
          media={mediaList}
          onClose={handlePreviewClose}
          open={isPreviewOpen}
          selectedMediaId={previewFileId}
        />
      )}
    </>
  );
};
